// MicroserviceGenerationDashboard.jsx
import React, { useState } from 'react';
import {
  ContentLayout,
  Container,
  Header,
  SpaceBetween,
  Tabs,
  Table,
  ButtonDropdown,
  Button,
  Box,
  Alert,
  BreadcrumbGroup,
  Link
} from '@cloudscape-design/components';
import ProjectMicroservices from './ProjectMicroservices';
import MicroserviceDetails from './MicroserviceDetails';

export function MicroserviceGenerationDashboard() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedMicroservice, setSelectedMicroservice] = useState(null);

  // Generate breadcrumb items based on current selection
  const getBreadcrumbItems = () => {
    const items = [
      { text: "Projects", href: "#/projects" }
    ];

    if (selectedProject) {
      items.push({
        text: selectedProject.name,
        href: `#/projects/${selectedProject.id}`
      });
    }

    if (selectedMicroservice) {
      items.push({
        text: selectedMicroservice.name,
        href: `#/projects/${selectedProject.id}/services/${selectedMicroservice.id}`
      });
    }

    return items;
  };

  // Example projects data
  const projects = [
    {
      id: 'proj1',
      name: 'E-Commerce Modernization',
      microservices: [
        {
          id: 'ms1',
          name: 'customer-service',
          domain: 'CustomerDomain',
          status: 'GENERATED',
          generatedAt: '2024-04-15T10:30:00Z',
          repository: 'github.com/org/customer-service',
          pipeline: 'jenkins/customer-service'
        },
        {
          id: 'ms2',
          name: 'order-service',
          domain: 'OrderDomain',
          status: 'IN_PROGRESS',
          generatedAt: '2024-04-15T11:00:00Z'
        }
      ]
    }
    // Add more projects
  ];

  return (
    <ContentLayout
      header={
        <SpaceBetween size="m">
          <BreadcrumbGroup
            items={getBreadcrumbItems()}
            ariaLabel="Breadcrumbs"
          />
          <Header
            variant="h1"
            description="Generate and manage microservices from domain specifications"
            actions={
              <Button variant="primary">
                Generate New Microservice
              </Button>
            }
          >
            Microservice Generation
          </Header>
        </SpaceBetween>
      }
    >
      {!selectedProject ? (
        <ProjectList 
          projects={projects}
          onSelectProject={setSelectedProject}
        />
      ) : !selectedMicroservice ? (
        <ProjectMicroservices
          project={selectedProject}
          onBack={() => setSelectedProject(null)}
          onSelectMicroservice={setSelectedMicroservice}
        />
      ) : (
        <MicroserviceDetails
          microservice={selectedMicroservice}
          onBack={() => setSelectedMicroservice(null)}
        />
      )}
    </ContentLayout>
  );
};

const ProjectList = ({ projects, onSelectProject }) => {
  return (
    <Table
      columnDefinitions={[
        {
          id: 'name',
          header: 'Project name',
          cell: item => (
            <Link onFollow={() => onSelectProject(item)}>
              {item.name}
            </Link>
          )
        },
        {
          id: 'microservices',
          header: 'Microservices',
          cell: item => `${item.microservices.length} services`
        },
        {
          id: 'status',
          header: 'Status',
          cell: item => {
            const completed = item.microservices.filter(m => m.status === 'GENERATED').length;
            const total = item.microservices.length;
            return `${completed}/${total} Generated`;
          }
        },
        {
          id: 'actions',
          header: 'Actions',
          cell: item => (
            <ButtonDropdown
              items={[
                { text: 'Generate new microservice', id: 'generate' },
                { text: 'View domain architecture', id: 'architecture' },
                { text: 'Export configuration', id: 'export' }
              ]}
            >
              Actions
            </ButtonDropdown>
          )
        }
      ]}
      items={projects}
      loadingText="Loading projects"
      selectionType="single"
      trackBy="id"
      variant="container"
    />
  );
};
